import React, { useMemo, useState } from 'react';
import Papa from 'papaparse';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { CalendarDays, Search, Filter, Download, BarChartBig } from 'lucide-react';

function ProductSalesPage() {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('quantity');
  const [viewMode, setViewMode] = useState('table');
  const [groupMode, setGroupMode] = useState('month'); // 'none', 'date', 'month'

  const [selectedProduct, setSelectedProduct] = useState('');
  const [showChart, setShowChart] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Завантаження даних при монтуванні
  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:8000`;
        const response = await fetch(`${apiBaseUrl}/service/get_product_history`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log(`✅ Завантажено ${data.length} записів продажів`);
        setHistoryData(data);
      } catch (error) {
        console.error('Error fetching product history:', error);
        alert(`Помилка завантаження даних: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const allCategories = useMemo(() => {
    const set = new Set(historyData.map(i => i.category));
    return Array.from(set);
  }, [historyData]);

  const filteredData = useMemo(() => {
    let filtered = historyData.map(item => ({
      ...item
    }));

    filtered = filtered.filter(item => {
      const matchProduct =
        item.product_name.toLowerCase().includes(search.toLowerCase()) ||
        String(item.product_id).includes(search);
      const matchCategory = !selectedCategory || item.category === selectedCategory;
      const matchStart = !startDate || item.date >= startDate;
      const matchEnd = !endDate || item.date <= endDate;
      return matchProduct && matchCategory && matchStart && matchEnd;
    });

    // Групування по датах або місяцях
    if (groupMode !== 'none') {
      const grouped = {};
      filtered.forEach(item => {
        let groupKey;
        if (groupMode === 'date') {
          groupKey = `${item.product_id}_${item.date}`;
        } else if (groupMode === 'month') {
          const yearMonth = item.date.substring(0, 7);
          groupKey = `${item.product_id}_${yearMonth}`;
        }
        
        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            ...item,
            date: groupMode === 'month' ? item.date.substring(0, 7) : item.date,
            quantity: 0,
            transactions_count: 0
          };
        }
        grouped[groupKey].quantity += item.quantity;
        grouped[groupKey].transactions_count += 1;
      });
      filtered = Object.values(grouped);
    }

    filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [search, startDate, endDate, selectedCategory, historyData, sortOrder, sortField, groupMode]);

  const csvUrl = useMemo(() => {
    if (filteredData.length === 0) return null;
    const csv = Papa.unparse(filteredData);
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
    return URL.createObjectURL(blob);
  }, [filteredData]);

  const start = (currentPage - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  const paginatedData = filteredData.slice(start, end);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const toggleSort = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const productOptions = useMemo(() => {
    return [...new Set(historyData.map(h => `${h.product_id} — ${h.product_name}`))];
  }, [historyData]);

  const chartData = useMemo(() => {
    const matchID = selectedProduct.split(' — ')[0];
    const chartFiltered = historyData
      .filter(h => String(h.product_id) === matchID)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return {
      labels: chartFiltered.map(d => d.date),
      datasets: [{
        label: selectedProduct,
        data: chartFiltered.map(d => d.quantity),
        borderColor: 'blue',
        tension: 0.1
      }]
    };
  }, [selectedProduct, historyData]);

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4">Історія продажів по товарам</h2>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="flex items-center border rounded px-2">
          <Search className="w-4 h-4 text-gray-500 mr-2" />
          <input
            type="text"
            placeholder="Пошук..."
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="py-2 outline-none w-full"
          />
        </div>

        <div className="flex items-center border rounded px-2">
          <CalendarDays className="w-4 h-4 text-gray-500 mr-2" />
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="py-2 outline-none w-full"
            placeholder="Від"
          />
        </div>

        <div className="flex items-center border rounded px-2">
          <CalendarDays className="w-4 h-4 text-gray-500 mr-2" />
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="py-2 outline-none w-full"
            placeholder="До"
          />
        </div>

        <div className="flex items-center border rounded px-2">
          <Filter className="w-4 h-4 text-gray-500 mr-2" />
          <select
            className="py-2 outline-none w-full"
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
          >
            <option value="">Усі категорії</option>
            {allCategories.sort().map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div className="flex gap-2">
          <select
            value={viewMode}
            onChange={e => setViewMode(e.target.value)}
            className="border p-2 rounded"
          >
            <option value="table">Таблиця</option>
            <option value="chart">Графік</option>
          </select>

          {viewMode === 'table' && (
            <select
              value={groupMode}
              onChange={e => setGroupMode(e.target.value)}
              className="border p-2 rounded bg-white"
            >
              <option value="month">📅 По місяцях</option>
              <option value="date">📆 По датах</option>
              <option value="none">📋 Деталізовано</option>
            </select>
          )}
        </div>
        {csvUrl && (
          <a href={csvUrl} download="product_sales.csv" className="flex items-center bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
            <Download className="w-4 h-4 mr-2" />
            Завантажити CSV
          </a>
        )}
      </div>

      {/* Chart Options */}
      {viewMode === 'chart' && (
        <div className="flex gap-3 mb-4 flex-wrap items-center">
          <div className="flex items-center border rounded px-2">
            <BarChartBig className="w-4 h-4 text-gray-500 mr-2" />
            <input
              list="products"
              className="py-2 outline-none w-64"
              placeholder="Введіть назву або ID продукту..."
              value={selectedProduct}
              onChange={e => {
                setSelectedProduct(e.target.value);
                setShowChart(false);
              }}
            />
            <datalist id="products">
              {productOptions.map(name => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>

          <button
            disabled={!selectedProduct}
            onClick={() => setShowChart(true)}
            className={`px-4 py-2 rounded text-white ${selectedProduct ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300 cursor-not-allowed'}`}
          >
            Показати графік
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="w-full bg-gray-100 rounded-full h-2.5 mb-4 animate-pulse">
          <div className="bg-blue-600 h-2.5 rounded-full w-1/2 animate-pulse"></div>
        </div>
      ) : viewMode === 'chart' && showChart ? (
        <div className="bg-white p-6 shadow rounded">
          {chartData.labels.length > 0 ? (
            <Line data={chartData} options={{
              responsive: true,
              plugins: {
                legend: { position: 'bottom' },
                title: { display: true, text: selectedProduct }
              },
              scales: {
                y: { title: { display: true, text: 'Кількість' } },
                x: { title: { display: true, text: 'Дата' } }
              }
            }} />
          ) : (
            <p className="text-center text-gray-500">Немає даних для цього продукту</p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed bg-white border text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th style={{ width: '80px' }} className="py-2 px-2 border cursor-pointer" onClick={() => { setSortField('product_id'); toggleSort(); }}>ID ↑↓</th>
                <th style={{ width: '280px' }} className="py-2 px-2 border cursor-pointer" onClick={() => { setSortField('product_name'); toggleSort(); }}>Продукт ↑↓</th>
                <th style={{ width: '160px' }} className="py-2 px-2 border cursor-pointer" onClick={() => { setSortField('category'); toggleSort(); }}>Категорія ↑↓</th>
                <th style={{ width: '120px' }} className="py-2 px-2 border cursor-pointer text-center" onClick={() => { setSortField('quantity'); toggleSort(); }}>Кількість ↑↓</th>
                <th style={{ width: '140px' }} className="py-2 px-2 border cursor-pointer" onClick={() => { setSortField('date'); toggleSort(); }}>{groupMode === 'month' ? 'Місяць' : 'Дата'} ↑↓</th>
                {groupMode !== 'none' && (
                  <th style={{ width: '100px' }} className="py-2 px-2 border cursor-pointer text-center" onClick={() => { setSortField('transactions_count'); toggleSort(); }}>Транзакцій ↑↓</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, idx) => (
                <tr key={idx}>
                  <td className="py-1 px-2 border text-center">{item.product_id}</td>
                  <td className="py-1 px-2 border truncate">{item.product_name}</td>
                  <td className="py-1 px-2 border">{item.category}</td>
                  <td className="py-1 px-2 border text-center">{Math.round(item.quantity)}</td>
                  <td className="py-1 px-2 border text-center">{item.date}</td>
                  {groupMode !== 'none' && (
                    <td className="py-1 px-2 border text-center">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        {item.transactions_count}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan={groupMode !== 'none' ? "6" : "5"} className="py-4 text-center text-gray-500">Результатів не знайдено</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="flex justify-between items-center mt-4 gap-4">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="bg-gray-200 px-3 py-1 rounded disabled:opacity-50"
            >
              ← Попередня
            </button>

            <div className="flex items-center gap-2">
              <span>Сторінка</span>
              <input
                type="number"
                value={currentPage}
                min={1}
                max={totalPages}
                onChange={(e) => {
                  const page = Math.min(totalPages, Math.max(1, parseInt(e.target.value) || 1));
                  setCurrentPage(page);
                }}
                className="border rounded px-2 py-1 w-16 text-center"
              />
              <span>з {totalPages}</span>
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="bg-gray-200 px-3 py-1 rounded disabled:opacity-50"
            >
              Наступна →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductSalesPage;

